/**
 * Destructive-action confirmation.
 *
 * Replaces `window.confirm`, which was reliable but rendered as browser chrome:
 * left-to-right, in the browser's language rather than the app's, with "OK"
 * and "Cancel" beside seventeen carefully written Hebrew questions. In an app
 * that is RTL and Hebrew throughout, the one dialog that asks before something
 * irreversible was the one that looked like it came from somewhere else.
 *
 * **Promise-based on purpose.** `modals.openConfirmModal` is callback-shaped,
 * which would have turned every `if (window.confirm(…)) remove()` into a
 * restructured handler — seventeen chances to change the control flow while
 * only meaning to change the dialog. Awaiting a boolean keeps each call site
 * the shape it already had:
 *
 *     if (!(await confirmDelete(`הפריט "${name}"`))) return
 *
 * `openConfirmModal` fires `onClose` after `onConfirm` as well as after a
 * dismissal, but a promise keeps its first settlement, so resolving `false`
 * there cannot overwrite a `true` that already happened. That is what makes
 * closing with Escape, the ×, or a click outside all correctly mean "no".
 */
import { modals } from '@mantine/modals'

interface ConfirmOptions {
  /** Dialog heading — what is about to happen, in a few words. */
  title: string
  /** The question itself. Newlines render as separate lines. */
  message: string
  /** Defaults to "אישור"; use the verb when there is a better one. */
  confirmLabel?: string
  /**
   * Red confirm button. On by default: everything routed through here is
   * destructive. Set false for a merely consequential question.
   */
  danger?: boolean
}

export function confirmAction({
  title, message, confirmLabel = 'אישור', danger = true,
}: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    modals.openConfirmModal({
      title,
      // `white-space: pre-line` so the callers that compose a multi-line
      // question — the BOM→PO conversion states the order total on its own
      // line — keep reading the way they were written.
      children: <div style={{ whiteSpace: 'pre-line' }}>{message}</div>,
      labels: { confirm: confirmLabel, cancel: 'ביטול' },
      confirmProps: danger ? { color: 'red' } : undefined,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
      onClose: () => resolve(false),
    })
  })
}

/**
 * The common case: "delete X, this cannot be undone".
 *
 * `what` is the thing, phrased to follow "למחוק את" — e.g. `הפריט "ABC-1"`.
 */
export function confirmDelete(what: string, extra?: string): Promise<boolean> {
  return confirmAction({
    title: 'אישור מחיקה',
    message: `למחוק את ${what}? הפעולה אינה הפיכה.${extra ? `\n\n${extra}` : ''}`,
    confirmLabel: 'מחק',
  })
}
